import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, MapPin, Users, Coffee, Share2, CheckCircle2, Settings } from "lucide-react";
import { useApi, money, media, copyShareLink } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page, Spinner, Empty } from "@/components/common";
import { fmtDate, CreatorPhoto } from "@/components/EventCard";
import { CheckoutDialog } from "@/components/CheckoutDialog";
import { EventReviews } from "@/components/Reviews";

const Row = ({ icon: Icon, children }) => (
  <div className="flex items-start gap-3 text-sm"><Icon className="h-4 w-4 mt-0.5 text-[#FF5C00] shrink-0" /><div>{children}</div></div>
);

const BookingCard = ({ e, user }) => {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const full = e.max_attendees && e.attendee_count >= e.max_attendees;
  const past = new Date(e.end_time) < new Date();
  const getTicket = () => (user ? setOpen(true) : nav(`/login?next=/events/${e.id}`));
  let cta;
  if (e.is_creator) cta = <Link to={`/manage/${e.id}`} data-testid="event-manage-btn" className="w-full inline-flex items-center justify-center gap-2 bg-stone-900 text-white font-semibold py-3.5 rounded-full hover:bg-[#FF5C00] transition-colors"><Settings className="h-4 w-4" /> Manage event</Link>;
  else if (e.viewer_signed_up) cta = <Link to="/tickets" data-testid="event-going-badge" className="w-full inline-flex items-center justify-center gap-2 bg-emerald-700 text-white font-semibold py-3.5 rounded-full"><CheckCircle2 className="h-4 w-4" /> You're going</Link>;
  else if (e.cancelled || past || full) cta = <div data-testid="event-unavailable" className="w-full text-center bg-stone-200 text-stone-600 font-semibold py-3.5 rounded-full">{e.cancelled ? "Cancelled" : past ? "Event ended" : "Sold out"}</div>;
  else cta = <button data-testid="event-get-ticket-btn" onClick={getTicket} className="w-full bg-[#FF5C00] hover:bg-[#E04E00] text-white font-semibold py-3.5 rounded-full transition-colors">Get ticket · {money(e.price)}</button>;

  return (
    <aside className="lg:sticky lg:top-24 rounded-3xl bg-white border border-stone-200 p-6 space-y-5 h-fit">
      <div className="flex items-baseline justify-between">
        <span data-testid="event-price" className="font-display text-4xl font-black">{money(e.price)}</span>
        <span className="font-mono text-xs text-stone-500">{e.attendee_count || 0}{e.max_attendees ? ` / ${e.max_attendees}` : ""} going</span>
      </div>
      {cta}
      <button data-testid="event-share-btn" onClick={() => copyShareLink(e.id)}
        className="w-full inline-flex items-center justify-center gap-2 border border-stone-300 hover:border-stone-900 text-sm font-semibold py-3 rounded-full transition-colors"><Share2 className="h-4 w-4" /> Share</button>
      {!user && <p className="text-xs text-stone-500 text-center">Log in with your Creator Series app account to buy tickets.</p>}
      <CheckoutDialog event={e} open={open} onOpenChange={setOpen} />
    </aside>
  );
};

export default function EventDetail() {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const { data, isLoading, error } = useApi(loading ? null : user ? `/events/${id}` : `/public/events/${id}`);
  const { data: mediaData } = useApi(user ? `/events/${id}/media` : null);
  if (loading || isLoading) return <Page><Spinner /></Page>;
  if (error || !data?.event) return <Page><Empty title="Event not found">It may be private or removed.</Empty></Page>;
  const e = data.event;
  const pics = (mediaData?.media || []).filter((m) => m.type !== "video");

  return (
    <Page>
      <div className="grid lg:grid-cols-[1fr_380px] gap-10">
        <div className="space-y-10 min-w-0">
          <Link to={e.creator ? `/creators/${e.creator.id}` : "#"} data-testid="event-creator-hero" className="rise group block relative rounded-3xl overflow-hidden aspect-[4/3] sm:aspect-[16/9] max-h-[420px] w-full bg-stone-200">
            <CreatorPhoto creator={e.creator} className="group-hover:scale-[1.02] transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-stone-950/90 via-stone-950/20 to-transparent" />
            <div className="absolute bottom-0 inset-x-0 p-6 sm:p-8 text-white">
              <p className="font-mono text-[11px] uppercase tracking-widest text-orange-300">Hosted by</p>
              <p data-testid="event-creator-name" className="font-display text-3xl sm:text-5xl font-black tracking-tight leading-none">{e.creator?.name}</p>
              <p className="mt-2 text-base sm:text-lg font-semibold text-white/95">{e.title}</p>
              {e.creator?.city && <p className="text-sm text-stone-300">{e.creator.city}</p>}
            </div>
          </Link>
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-[#C2410C]">{fmtDate(e.start_time, "EEEE, MMMM d")}</p>
            <h1 data-testid="event-title" className="mt-2 font-display text-3xl sm:text-4xl font-black tracking-tight leading-tight break-words">{e.title}</h1>
            {e.cancelled && <p className="mt-3 inline-block font-mono text-xs bg-red-600 text-white px-3 py-1 rounded-full">This event was cancelled</p>}
          </div>
          {e.flyer && <img data-testid="event-flyer" src={media(e.flyer)} alt="Event flyer" className="w-full max-w-md rounded-2xl border border-stone-200" />}
          <div className="grid sm:grid-cols-2 gap-4 p-6 rounded-3xl bg-white border border-stone-200">
            <Row icon={CalendarDays}>{fmtDate(e.start_time, "MMM d, h:mm a")} – {fmtDate(e.end_time, "h:mm a")}</Row>
            <Row icon={MapPin}><span className="font-semibold">{e.location_name}</span>{e.address && <span className="block text-stone-500">{e.address}</span>}</Row>
            <Row icon={Users}>{e.attendee_count || 0} going{e.max_attendees ? ` · ${e.max_attendees} max` : ""}{e.min_attendees ? ` · ${e.min_attendees} min` : ""}</Row>
            {e.refreshments && <Row icon={Coffee}>{e.refreshments}</Row>}
          </div>
          {e.description && <p data-testid="event-description" className="text-base leading-relaxed text-stone-700 whitespace-pre-line">{e.description}</p>}
          {pics.length > 0 && (
            <section>
              <h3 className="font-display text-2xl font-bold mb-4">Photos</h3>
              <div className="grid grid-cols-3 gap-2">
                {pics.slice(0, 9).map((m) => <img key={m.id} src={media(m.url)} alt="" onError={(ev) => (ev.currentTarget.style.display = "none")} className="aspect-square object-cover rounded-xl bg-stone-200" />)}
              </div>
            </section>
          )}
          {user && <EventReviews eventId={e.id} canReview={e.viewer_signed_up && new Date(e.start_time) < new Date()} />}
        </div>
        <BookingCard e={e} user={user} />
      </div>
    </Page>
  );
}
